import { ChevronLeft, ChevronRight, Download, MessageSquare, Share2, X } from "lucide-react";
import { useCallback, useEffect, useState } from "react";

import { downloadFromUrl } from "~/shared/lib/download";
import { MediaSurface, type MediaSurfaceProps } from "~/shared/ui/media-surface";

/** Один слайд лайтбокса — та же тройка полей, что рендерит примитив `MediaSurface`. */
export type MediaLightboxItem = Pick<MediaSurfaceProps, "url" | "type" | "caption">;

interface MediaLightboxProps {
    items: MediaLightboxItem[];
    initialIndex?: number;
    isOpen: boolean;
    onClose: () => void;
    /**
     * Показывать ли кнопку «Поделиться» (Share2) в шапке. По умолчанию false.
     * Включено в галерее вложений сообщений (бывший ImageLightbox) — заглушка
     * под будущий share-флоу.
     */
    showShare?: boolean;
    /**
     * Открыть источник текущего слайда (например, чат-генерации в библиотеке
     * «Ассеты»). Получает индекс текущего слайда — caller сам решает, куда вести.
     * Не задан → кнопка не рендерится (доменно-нейтрально для прочих вызовов).
     */
    onOpenSource?: (index: number) => void;
    /** aria-label кнопки «Открыть источник»; обязателен, когда задан `onOpenSource`. */
    openSourceLabel?: string;
    /**
     * Текущий слайд сменился. Нужен там, где `items` — лишь загруженное окно
     * ленты: caller подтягивает соседей, когда листание подходит к краю окна.
     */
    onIndexChange?: (index: number) => void;
    /**
     * Позиция окна `items` в полной ленте и её размер — для счётчика. Не заданы →
     * счётчик считает по самому `items` (обычный случай: лента и есть окно).
     */
    indexOffset?: number;
    totalCount?: number;
}

// Универсальный лайтбокс-карусель над лентой медиа (image + video в одной
// ленте): текущий слайд рендерится картинкой или видео по `type`,
// навигация/счётчик/стрелки/клавиши общие. Объединяет прежние ImageLightbox,
// VideoLightbox и MediaLightbox в один компонент: листать все сгенерированные
// объекты влево/вправо, не закрывая окно.
export function MediaLightbox({
    items,
    initialIndex = 0,
    isOpen,
    onClose,
    showShare = false,
    onOpenSource,
    openSourceLabel,
    onIndexChange,
    indexOffset = 0,
    totalCount,
}: MediaLightboxProps) {
    const [currentIndex, setCurrentIndex] = useState(initialIndex);

    useEffect(() => {
        setCurrentIndex(initialIndex);
    }, [initialIndex]);

    // Сообщаем ТОЛЬКО о переходе, сделанном пользователем. Репорт из эффекта по
    // самому `currentIndex` был бы не тем же самым: он срабатывает и на
    // синхронизацию с `initialIndex`, причём со СТАРЫМ индексом (обновление
    // применяется в следующем коммите) — caller, который по этому индексу
    // подменяет `initialIndex`, получал бы откат к нулевому слайду и зацикливание.
    const goTo = useCallback(
        (next: number) => {
            setCurrentIndex(next);
            onIndexChange?.(next);
        },
        [onIndexChange],
    );

    const showPrev = useCallback(
        () => goTo(currentIndex > 0 ? currentIndex - 1 : items.length - 1),
        [goTo, currentIndex, items.length],
    );
    const showNext = useCallback(
        () => goTo(currentIndex < items.length - 1 ? currentIndex + 1 : 0),
        [goTo, currentIndex, items.length],
    );

    useEffect(() => {
        if (!isOpen) return;
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape") onClose();
            if (e.key === "ArrowLeft") showPrev();
            if (e.key === "ArrowRight") showNext();
        };
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [isOpen, onClose, showPrev, showNext]);

    if (!isOpen) return null;
    const current = items[currentIndex];
    if (!current) return null;

    return (
        // Backdrop: click anywhere outside the media / controls closes the lightbox.
        // Inner blocks call stopPropagation so clicks on the media / nav / actions
        // don't bubble up and close it accidentally.
        <div
            role="dialog"
            aria-modal="true"
            onClick={onClose}
            className="animate-in fade-in fixed inset-0 z-50 flex items-center justify-center bg-black/95 duration-200"
        >
            <div
                className="absolute top-0 right-0 left-0 z-10 flex items-center justify-between p-4 text-zinc-400"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="text-sm font-medium">
                    {indexOffset + currentIndex + 1} / {totalCount ?? items.length}
                </div>
                <div className="flex items-center gap-2">
                    {onOpenSource && (
                        <button
                            type="button"
                            onClick={() => onOpenSource(currentIndex)}
                            aria-label={openSourceLabel ?? "Open source"}
                            className="rounded-full p-2 transition-colors hover:bg-zinc-800"
                        >
                            <MessageSquare className="h-5 w-5" />
                        </button>
                    )}
                    <button
                        type="button"
                        onClick={() =>
                            void downloadFromUrl(current.url).catch((err) =>
                                console.error("download failed", err),
                            )
                        }
                        aria-label="Download"
                        className="rounded-full p-2 transition-colors hover:bg-zinc-800"
                    >
                        <Download className="h-5 w-5" />
                    </button>
                    {showShare && (
                        <button
                            type="button"
                            className="rounded-full p-2 transition-colors hover:bg-zinc-800"
                        >
                            <Share2 className="h-5 w-5" />
                        </button>
                    )}
                    <button
                        type="button"
                        onClick={onClose}
                        className="ml-2 rounded-full p-2 transition-colors hover:bg-zinc-800"
                        aria-label="Close"
                    >
                        <X className="h-6 w-6" />
                    </button>
                </div>
            </div>

            <div className="relative flex h-full w-full items-center justify-center p-4 md:p-12">
                <MediaSurface
                    url={current.url}
                    type={current.type}
                    caption={current.caption}
                    autoPlay
                    onClick={(e) => e.stopPropagation()}
                />
            </div>

            {items.length > 1 && (
                <>
                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            showPrev();
                        }}
                        className="absolute left-4 rounded-full bg-black/50 p-3 text-white backdrop-blur-md transition-all hover:scale-105 hover:bg-black/70"
                        aria-label="Previous"
                    >
                        <ChevronLeft className="h-6 w-6" />
                    </button>
                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            showNext();
                        }}
                        className="absolute right-4 rounded-full bg-black/50 p-3 text-white backdrop-blur-md transition-all hover:scale-105 hover:bg-black/70"
                        aria-label="Next"
                    >
                        <ChevronRight className="h-6 w-6" />
                    </button>
                </>
            )}
        </div>
    );
}
